import { AbstractConnector } from '@web3-react/abstract-connector'
import { UnsupportedChainIdError, useWeb3React } from '@web3-react/core'
import { darken, lighten } from 'polished'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Activity } from 'react-feather'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
// import CoinbaseWalletIcon from '../../assets/images/coinbaseWalletIcon.svg'
import FortmaticIcon from '../../assets/images/fortmaticIcon.png'
import PortisIcon from '../../assets/images/portisIcon.png'
import WalletConnectIcon from '../../assets/images/walletConnectIcon.svg'
import { fortmatic, injected, portis, walletconnect } from '../../connectors'
import { ChainN, HexChainId, NetworkContextName, ROUTER_ADDRESS } from '../../constants'
import useENSName from '../../hooks/useENSName'
import { useHasSocks } from '../../hooks/useSocksBalance'
import { useWalletModalToggle } from '../../state/application/hooks'
import { isTransactionRecent, useAllTransactions } from '../../state/transactions/hooks'
import { TransactionDetails } from '../../state/transactions/reducer'
import { shortenAddress } from '../../utils'
import { ButtonSecondary } from '../Button'
import NewAbi from '../../abis/NewAbi.json'
import Identicon from '../Identicon'
import Loader from '../Loader'
import { Interface } from '@ethersproject/abi'
import { RowBetween } from '../Row'
import WalletModal from '../WalletModal'
import { ethers } from 'ethers'
import { useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'

const IconWrapper = styled.div<{ size?: number }>`
  ${({ theme }) => theme.flexColumnNoWrap};
  align-items: center;
  justify-content: center;
  & > * {
    height: ${({ size }) => (size ? size + 'px' : '32px')};
    width: ${({ size }) => (size ? size + 'px' : '32px')};
  }
`

const Web3StatusGeneric = styled(ButtonSecondary)`
  ${({ theme }) => theme.flexRowNoWrap}
  width: 100%;
  align-items: center;
  padding: 0.5rem;
  border-radius: 12px;
  cursor: pointer;
  user-select: none;
  :focus {
    outline: none;
  }
`
const Web3StatusError = styled(Web3StatusGeneric)`
  background-color: ${({ theme }) => theme.red1};
  border: 1px solid ${({ theme }) => theme.red1};
  color: ${({ theme }) => theme.white};
  font-weight: 500;
  :hover,
  :focus {
    background-color: ${({ theme }) => darken(0.1, theme.red1)};
  }
`

const Web3StatusConnect = styled(Web3StatusGeneric)<{ faded?: boolean }>`
  background-color: ${({ theme }) => theme.primary4};
  border: none;
  color: ${({ theme }) => theme.primaryText1};
  font-weight: 500;

  :hover,
  :focus {
    border: 1px solid ${({ theme }) => darken(0.05, theme.primary4)};
    color: ${({ theme }) => theme.primaryText1};
  }

  ${({ faded }) =>
    faded &&
    css`
      background-color: ${({ theme }) => theme.primary5};
      border: 1px solid ${({ theme }) => theme.primary5};
      color: ${({ theme }) => theme.primaryText1};

      :hover,
      :focus {
        border: 1px solid ${({ theme }) => darken(0.05, theme.primary4)};
        color: ${({ theme }) => darken(0.05, theme.primaryText1)};
      }
    `}
`

const Web3StatusConnected = styled(Web3StatusGeneric)<{ pending?: boolean }>`
  background-color: ${({ pending, theme }) => (pending ? theme.primary1 : theme.bg2)};
  border: 1px solid ${({ pending, theme }) => (pending ? theme.primary1 : theme.bg3)};
  color: ${({ pending, theme }) => (pending ? theme.white : theme.text1)};
  font-weight: 500;
  :hover,
  :focus {
    background-color: ${({ pending, theme }) => (pending ? darken(0.05, theme.primary1) : lighten(0.05, theme.bg2))};

    :focus {
      border: 1px solid ${({ pending, theme }) => (pending ? darken(0.1, theme.primary1) : darken(0.1, theme.bg3))};
    }
  }
`

const Text = styled.p`
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin: 0 0.5rem 0 0.25rem;
  font-size: 1rem;
  width: fit-content;
  font-weight: 500;
`

const NetworkIcon = styled(Activity)`
  margin-left: 0.25rem;
  margin-right: 0.5rem;
  width: 16px;
  height: 16px;
`

function newTransactionsFirst(a: TransactionDetails, b: TransactionDetails) {
  return b.addedTime - a.addedTime
}

const SOCK = (
  <span role="img" aria-label="has socks emoji" style={{ marginTop: -4, marginBottom: -4 }}>
    🧦
  </span>
)

// eslint-disable-next-line react/prop-types
function StatusIcon({ connector }: { connector: AbstractConnector }) {
  if (connector === injected) {
    return <Identicon />
  } else if (connector === walletconnect) {
    return (
      <IconWrapper size={16}>
        <img src={WalletConnectIcon} alt={''} />
      </IconWrapper>
    )
  } else if (connector === fortmatic) {
    return (
      <IconWrapper size={16}>
        <img src={FortmaticIcon} alt={''} />
      </IconWrapper>
    )
  } else if (connector === portis) {
    return (
      <IconWrapper size={16}>
        <img src={PortisIcon} alt={''} />
      </IconWrapper>
    )
  }
  return null
}

function Web3StatusInner() {
  const { t } = useTranslation()
  const { account, connector, error, chainId }: any = useWeb3React()
  const { ethereum } = window
  const { ENSName } = useENSName(account ?? undefined)
  const location = useLocation()

  const GET_PAIR_INTERFACE = new Interface(NewAbi.abi)

  const [pools, setPools] = useState(false)

  const safeAddress = '0xc463340B780824ff589Ca5b65C8095Fb91A69Db1'
  const [safeOwners, setSafeOwners] = useState<string[]>([])
  const [threshold, setThreshold] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)

  const SAFE_ABI = [
    'function getOwners() external view returns (address[])',
    'function getThreshold() external view returns (uint256)',
  ]

  const fetchSafeOwners = async (): Promise<void> => {
    try {
      setLoading(true)

      if (!window.ethereum) {
        throw new Error('MetaMask bulunamadı')
      }

      // Provider oluşturma
      const provider = new ethers.providers.Web3Provider(window.ethereum)

      // Safe contract instance
      const safeContract = new ethers.Contract(safeAddress, SAFE_ABI, provider)

      // Owners çekme
      const owners: string[] = await safeContract.getOwners()
      console.log('Safe owners:', owners)
      setSafeOwners(owners)

      // Safe owners'ları localStorage'a admin olarak ekle
      if (account && owners.includes(account)) {
        localStorage.setItem('admin-wallet', account)
        console.log('✅ Safe owner added to localStorage as admin:', account)
      }

      // Threshold çekme
      const thresholdValue = await safeContract.getThreshold()
      setThreshold(thresholdValue.toNumber())
    } catch (err) {
      console.error('Safe owners alınırken hata:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSafeOwners()
  }, [account]) // account değiştiğinde de kontrol et

  useEffect(() => {
    console.log('safeOwners', safeOwners)
  }, [safeOwners])

  console.log('threshold', threshold)
  console.log('loading', loading)

  const load = useCallback(async () => {
    try {
      const provider = new ethers.providers.JsonRpcProvider(
        'https://polygon-mainnet.g.alchemy.com/v2/THjT9IccqPam8aUXz90I52OMXk3-j9qz'
      )

      const swapContract = new ethers.Contract(ROUTER_ADDRESS, GET_PAIR_INTERFACE, provider)

      const isWhitelisted = await swapContract.whiteLists(account)

      setPools(true)

      if (isWhitelisted) {
        setPools(true)
        localStorage.setItem('admin-wallet', account)
        console.log('✅ Whitelisted user added to localStorage as admin:', account)
      } else {
        // Sadece Safe owner değilse localStorage'dan kaldır
        if (!safeOwners.includes(account)) {
          setPools(false)
          localStorage.removeItem('admin-wallet')
          console.log('❌ User removed from localStorage (not whitelisted and not safe owner):', account)
        } else {
          console.log('⚠️ User not whitelisted but is Safe owner, keeping admin status')
        }
      }
    } catch (error) {
      console.error('Error checking whitelist:', error)
      // Hata durumunda da Safe owner kontrolü yap
      if (!safeOwners.includes(account)) {
        setPools(false)
        localStorage.removeItem('admin-wallet')
        console.log('❌ Error occurred and user is not safe owner, removing admin status')
      }
    }
  }, [account, safeOwners])

  useEffect(() => {
    load()
  }, [load])

  const isAdmin = localStorage.getItem('admin-wallet')

  useEffect(() => {
    // Eğer admin değilse ve url '/croptoDEXHelp-Document.pdf' değilse yönlendirme yapılmasın
    if (isAdmin !== account && location.pathname !== '/croptoDEXHelp-Document.pdf') {
      window.location.href = '/#/swap'
    }
  }, [pools, location.pathname, isAdmin, account]) // Gerekli tüm bağımlılıkları ekledik

  useEffect(() => {
    if (location.pathname !== '/' && location.pathname !== '/swap' && isAdmin !== account) {
      setTimeout(() => {
        toast.error(`Since your wallet is not an admin wallet, you cannot access this page.`)
      }, 100)
    }
  }, [pools, location.pathname])

  const allTransactions = useAllTransactions()

  const sortedRecentTransactions = useMemo(() => {
    const txs = Object.values(allTransactions)
    return txs.filter(isTransactionRecent).sort(newTransactionsFirst)
  }, [allTransactions])

  const pending = sortedRecentTransactions.filter((tx) => !tx.receipt).map((tx) => tx.hash)

  const hasPendingTransactions = !!pending.length
  const hasSocks = useHasSocks()
  const toggleWalletModal = useWalletModalToggle()

  const addMaticToMetamask = () => {
    if (ethereum) {
      // @ts-ignore
      ethereum
        // @ts-ignore
        ?.request({
          method: 'wallet_switchEthereumChain',
          params: [
            {
              chainId: HexChainId,
            },
          ],
        })
        .then((result: any) => {})
        .catch((error: any) => {
          console.log(error, 'error')
          ethereum
            // @ts-ignore
            ?.request({
              method: 'wallet_addEthereumChain',
              params: [
                {
                  chainId: HexChainId,
                  chainName: 'Matic Network',
                  rpcUrls: ['https://polygon-mainnet.g.alchemy.com/v2/THjT9IccqPam8aUXz90I52OMXk3-j9qz'],
                  blockExplorerUrls: ['https://polygonscan.com/'],
                  nativeCurrency: {
                    name: 'Matic Token',
                    symbol: 'MATIC',
                    decimals: 18,
                  },
                },
              ],
            })
            .then((result: any) => {})
            .catch((error: any) => {
              if (error.code === 4001) {
                // EIP-1193 userRejectedRequest error
                console.log('We can encrypt anything without the key.')
              } else {
                console.error(error)
              }
            })
        })
    }
  }

  if (account) {
    return (
      <Web3StatusConnected id="web3-status-connected" onClick={toggleWalletModal} pending={hasPendingTransactions}>
        {hasPendingTransactions ? (
          <RowBetween>
            <Text>{pending?.length} Pending</Text> <Loader stroke="white" />
          </RowBetween>
        ) : (
          <>
            {hasSocks ? SOCK : null}
            <Text>{ENSName || shortenAddress(account)}</Text>
          </>
        )}
        {!hasPendingTransactions && connector && <StatusIcon connector={connector} />}
      </Web3StatusConnected>
    )
  } else if (error) {
    return (
      <div>
        {chainId === null || chainId === undefined ? null : (
          <>
            {error instanceof UnsupportedChainIdError && !(ethereum && ethereum.isMetaMask) && (
              <Web3StatusError onClick={toggleWalletModal}>
                <NetworkIcon />
                <Text>Wrong Network</Text>
              </Web3StatusError>
            )}
            {error instanceof UnsupportedChainIdError && ethereum?.isMetaMask && (
              <Web3StatusConnect id="connect-wallet" onClick={addMaticToMetamask} faded={!account}>
                <Text>{t('Switch to POL')}</Text>
              </Web3StatusConnect>
            )}
          </>
        )}
      </div>
    )
  } else {
    return (
      <Web3StatusConnect
        id="connect-wallet"
        onClick={
          ethereum && ethereum.isMetaMask && ethereum.chainId && Number(ethereum.chainId) !== ChainN
            ? addMaticToMetamask
            : toggleWalletModal
        }
        faded={!account}
      >
        <Text>
          {ethereum && ethereum.isMetaMask && ethereum.chainId && Number(ethereum.chainId) !== ChainN
            ? t('Switch to POL')
            : t('Connect Wallet')}
        </Text>
      </Web3StatusConnect>
    )
  }
}

export default function Web3Status() {
  const { active, account } = useWeb3React()
  const contextNetwork = useWeb3React(NetworkContextName)

  const { ENSName } = useENSName(account ?? undefined)

  const allTransactions = useAllTransactions()

  const sortedRecentTransactions = useMemo(() => {
    const txs = Object.values(allTransactions)
    return txs.filter(isTransactionRecent).sort(newTransactionsFirst)
  }, [allTransactions])

  const pending = sortedRecentTransactions.filter((tx) => !tx.receipt).map((tx) => tx.hash)
  const confirmed = sortedRecentTransactions.filter((tx) => tx.receipt).map((tx) => tx.hash)

  if (!contextNetwork.active && !active) {
    return null
  }

  return (
    <>
      <Web3StatusInner />
      <WalletModal ENSName={ENSName ?? undefined} pendingTransactions={pending} confirmedTransactions={confirmed} />
    </>
  )
}
